import config, { Country } from 'config';
import { DELIVERY } from 'config/api-routes';
import { DeliveryType, DeliveryWithAdditionalDataType } from 'types/delivery.types';
import { AddressPickupPoints } from 'utils/address-pickup-points';
import i18n from 'utils/i18n';
import { GET, POST } from 'utils/request';
import { formatResponse } from 'utils/service-helpers';

const currentCountry = config.get('country');

export type DeliveryMoreInfoLink = {
  title: string;
  link: string;
};

interface DeliveryOptionAdditionalInfo {
  iconSrc?: string;
  tooltip?: string;
  moreInfo: DeliveryMoreInfoLink[];
  moreInfoIntegration?: DeliveryMoreInfoLink[];
}

export type PargoLocation = {
  pickupPointCode: string;
  name: string;
  openingHours: string;
  address: string;
  city: string;
  suburb: string;
  postalCode: string;
};

export type MtaaniAgent = {
  id: number;
  businessName: string;
};

export type MtaaniArea = {
  id: number;
  name: string;
};

export type MtaaniLocation = {
  id: number;
  name: string;
};

export default class DeliveryService {
  getProvinces(country: Country = currentCountry): Array<string> {
    // TODO: These should come from API
    const provinces = {
      [Country.EE]: [
        'Tallinn/Harjumaa',
        'Saaremaa',
        'Jõgevamaa',
        'Hiiumaa',
        'Ida-Virumaa',
        'Järvamaa',
        'Pärnumaa',
        'Põlvamaa',
        'Lääne-Virumaa',
        'Raplamaa',
        'Tartumaa',
        'Läänemaa',
        'Valgamaa',
        'Viljandimaa',
        'Võrumaa',
      ],
      [Country.LV]: ['Kurzeme', 'Latgale', 'Riga', 'Vidzeme', 'Zemgale'],
      [Country.ZA]: [
        'Eastern Cape',
        'Free State',
        'Gauteng',
        'KwaZulu-Natal',
        'Limpopo',
        'Mpumalanga',
        'North West',
        'Northern Cape',
        'Western Cape',
      ],
      [Country.KE]: ['Coast', 'North Eastern', 'Eastern', 'Central', 'Rift Valley', 'Western', 'Nyanza', 'Nairobi'],
      [Country.EG]: [
        'Cairo',
        'Alexandria',
        'Aswan',
        'Asyut',
        'Beheira',
        'Beni Suef',
        'Dakahlia',
        'Damietta',
        'Faiyum',
        'Gharbia',
        'Giza',
        'Ismailia',
        'Kafr El Sheikh',
        'Luxor',
        'Matrouh',
        'Minya',
        'Monufia',
        'New Valley',
        'North Sinai',
        'Port Said',
        'Qalyubia',
        'Qena',
        'Red Sea',
        'Sharqia',
        'Sohag',
        'South Sinai',
        'Suez',
      ],
    };

    return provinces[country] || [];
  }

  async getDeliveryOptions(opts = {}): Promise<DeliveryWithAdditionalDataType> {
    const additionalInfo = getDeliveryAdditionalInfo();
    const deliveryOptions: Array<DeliveryType> = await GET(DELIVERY.OPTIONS.GET(), opts);

    return formatResponse(deliveryOptions).map((deliveryOption) => {
      const foundDeliveryOption = additionalInfo[deliveryOption.name];

      return {
        ...(foundDeliveryOption || {}),
        ...(deliveryOption || {}),
        moreInfo: resolveMoreInfo(foundDeliveryOption, deliveryOption.withDoorcode),
        tooltip: deliveryOption?.tooltipText,
      };
    });
  }

  getDeliveryMoreInfo(name: string, withDoorcode = false): DeliveryMoreInfoLink[] {
    return resolveMoreInfo(getDeliveryAdditionalInfo()[name], withDoorcode) ?? [];
  }

  async getDeliveryLocations(deliveryServiceName: string, country?: Country, size?: string, opts = {}) {
    const response = await GET(DELIVERY.LOCATION.GET(deliveryServiceName, country, size), opts);

    return formatResponse(response);
  }

  async getAddressPickupPoints(methods: string[], size?: string): Promise<AddressPickupPoints> {
    try {
      const response = await GET(DELIVERY.ADDRESS_PICKUP_POINTS.GET(methods, size), { timeout: 1000, silentFail: true });
      const data = formatResponse(response);

      return data && typeof data === 'object' && !Array.isArray(data) ? data : {};
    } catch {
      return {};
    }
  }

  async getDeliveryPrice(deliveryServiceName: string, data, opts = {}) {
    const response = await POST(DELIVERY.PRICE.POST(deliveryServiceName), data, opts);

    return formatResponse(response);
  }

  async getPargoLocation(locationId: string, opts = {}): Promise<PargoLocation> {
    const response = await GET(DELIVERY.OPTIONS.PARGO.LOCATION.GET(locationId), opts);

    return formatResponse(response);
  }

  async getMtaaniNairobiCBDAgents(opts = {}): Promise<MtaaniAgent[]> {
    const response = await GET(DELIVERY.OPTIONS.MTAANI.NAIROBICBDAGENTS.GET(), opts);

    return formatResponse(response);
  }

  async getMtaaniAgents(locationId: number, opts = {}): Promise<MtaaniAgent[]> {
    const response = await GET(DELIVERY.OPTIONS.MTAANI.AGENTS.GET(locationId), opts);

    return formatResponse(response);
  }

  async getMtaaniAgent(agentId: number, locationId: number, opts = {}): Promise<MtaaniAgent> {
    const response = await GET(DELIVERY.OPTIONS.MTAANI.AGENTS.GET(locationId), opts);

    const allAgents = formatResponse(response);

    const agent = allAgents.find((a: MtaaniAgent) => a.id === agentId);

    if (!agent && !(opts as any).silentFail) {
      throw new Error(`Cannot find agent with ID ${agentId}`);
    }

    return agent;
  }

  async getMtaaniLocations(areaId: number, opts = {}): Promise<MtaaniLocation[]> {
    const response = await GET(DELIVERY.OPTIONS.MTAANI.LOCATIONS.GET(areaId), opts);

    return formatResponse(response);
  }

  async getMtaaniLocation(locationId: number, opts = {}): Promise<MtaaniLocation> {
    const response = await GET(DELIVERY.OPTIONS.MTAANI.LOCATION.GET(locationId), opts);

    return formatResponse(response);
  }

  async getMtaaniAreas(opts = {}): Promise<MtaaniArea[]> {
    const response = await GET(DELIVERY.OPTIONS.MTAANI.AREAS.GET(), opts);

    return formatResponse(response);
  }

  async getMtaaniArea(areaId: number, opts = {}): Promise<MtaaniArea> {
    const response = await GET(DELIVERY.OPTIONS.MTAANI.AREA.GET(areaId), opts);

    return formatResponse(response);
  }

  async getMtaaniPrice(senderId: number, receiverId: number, productPrice: number, opts = {}) {
    const response = await GET(DELIVERY.OPTIONS.MTAANI.PRICE_INFO.GET(senderId, receiverId, productPrice), opts);

    return formatResponse(response);
  }
}

function getDeliveryAdditionalInfo(): Record<string, DeliveryOptionAdditionalInfo> {
  const packageSizeInfo = (link: string) => [{ title: i18n.t('components.delivery.package-size-more-info'), link: i18n.t(link) }];
  const howItWorksInfo = (title: string, link: string) => [{ title: i18n.t(title), link: i18n.t(link) }];

  return {
    omniva: { moreInfo: packageSizeInfo('shipping.moreInfo.omniva.link') },
    smartpost: { moreInfo: packageSizeInfo('shipping.moreInfo.smartpost.link') },
    dpd: { moreInfo: packageSizeInfo('shipping.moreInfo.dpd.link') },
    paxi: {
      moreInfo: howItWorksInfo('components.delivery.how-paxi-works', 'shipping.moreInfo.paxi.link'),
      moreInfoIntegration: howItWorksInfo('components.delivery.how-paxi-integration-works', 'shipping.moreInfo.paxi.integrationLink'),
    },
    // NOTE: Pudo and Postnet links are also hardcoded in buy/index.ts
    pudo: { moreInfo: howItWorksInfo('components.delivery.how-pudo-works', 'shipping.moreInfo.pudo.link') },
    postnet: { moreInfo: howItWorksInfo('components.delivery.how-postnet-works', 'shipping.moreInfo.postnet.link') },
    aramex: { moreInfo: howItWorksInfo('components.delivery.how-aramex-works', 'shipping.moreInfo.aramex.link') },
    pargo: { moreInfo: howItWorksInfo('components.delivery.how-pargo-works', 'shipping.moreInfo.pargo.link') },
    mtaani: { moreInfo: howItWorksInfo('components.delivery.how-mtaani-works', 'shipping.moreInfo.mtaani.link') },
    fargo: { moreInfo: howItWorksInfo('components.delivery.how-fargo-works', 'shipping.moreInfo.fargo.link') },
    bundling: {
      iconSrc: '/icons/icn-shipping.svg',
      moreInfo: howItWorksInfo('components.delivery.how-bundling-works', 'shipping.moreInfo.bundling.link'),
    },
  };
}

function resolveMoreInfo(info: DeliveryOptionAdditionalInfo | undefined, withDoorcode?: boolean): DeliveryMoreInfoLink[] | undefined {
  if (withDoorcode && info?.moreInfoIntegration) {
    return info.moreInfoIntegration;
  }

  return info?.moreInfo;
}
